import { isEmpty } from 'lodash'
import { addYears, isAfter, isSameMonth, max } from 'date-fns'
import { ClinicAvailabilities } from 'src/services/api/availabilities'
import { Availabilities } from '@customTypes/Availabilities'
import { getDateWithDisplayedMonthAndYearByDate } from 'src/utils/getDateWithDisplayedMonthAndYearByDate/getDateWithDisplayedMonthAndYearByDate'
import { GroupedAvailabilityMonth } from 'src/utils/groupAvailabilitiesByWeeks/groupAvailabilitiesByWeeks'
import { AvailabilitiesPeriod } from '@context/AppointmentCalendarContext/types'

const shouldFetchMoreWithCurrentPeriod = (
  currentPeriod: Availabilities[],
  lastLoadedMonth: GroupedAvailabilityMonth
): boolean => {
  const isLastLoadedMonthEmptyAndBeforeYearFromNow =
    !lastLoadedMonth.weeks.some((week) => week.some((day) => day.availabilities.length)) &&
    !isAfter(lastLoadedMonth.fromDate, addYears(new Date(), 1))
  if (isLastLoadedMonthEmptyAndBeforeYearFromNow) {
    return true
  }

  const currentPeriodsMonth = new Date(currentPeriod[currentPeriod.length - 1].date)
  const isCurrentPeriodInLastLoadedMonth = isSameMonth(currentPeriodsMonth, lastLoadedMonth.toDate)

  if (isCurrentPeriodInLastLoadedMonth) {
    return true
  }

  return false
}

const shouldFetchMoreWithoutCurrentPeriodWithSelection = (
  selectionDate: Date,
  lastLoadedMonth: GroupedAvailabilityMonth
): boolean => {
  const isSelectionTimeInLastLoadedMonth = isSameMonth(
    getDateWithDisplayedMonthAndYearByDate(selectionDate),
    lastLoadedMonth.toDate
  )
  if (isSelectionTimeInLastLoadedMonth) {
    return false
  }

  const bailOutDate = max([addYears(new Date(), 1), selectionDate])
  const isBailOutDateReached = isAfter(lastLoadedMonth.fromDate, bailOutDate)
  if (isBailOutDateReached) {
    return false
  }

  return true
}

const shouldFetchMoreWithoutCurrentPeriodWithoutSelection = (lastLoadedMonth: GroupedAvailabilityMonth): boolean => {
  const isLastLoadedMonthPopulated = lastLoadedMonth.weeks.some((week) => week.some((day) => day.availabilities.length))
  if (isLastLoadedMonthPopulated) {
    return false
  }

  const isLastLoadedMonthAfterYearFromNow = isAfter(lastLoadedMonth.fromDate, addYears(new Date(), 1))
  if (isLastLoadedMonthAfterYearFromNow) {
    return false
  }

  return true
}

const shouldFetchMoreWithoutCurrentPeriod = (
  lastLoadedMonth: GroupedAvailabilityMonth,
  selectionDate?: Date
): boolean => {
  if (selectionDate) {
    return shouldFetchMoreWithoutCurrentPeriodWithSelection(selectionDate, lastLoadedMonth)
  }
  return shouldFetchMoreWithoutCurrentPeriodWithoutSelection(lastLoadedMonth)
}

export const getShouldFetchMore = (
  currentPeriod: AvailabilitiesPeriod,
  selectionDate?: Date,
  data?: ClinicAvailabilities[],
  isFetchingMore?: boolean,
  periodRangeStart?: string
): boolean => {
  if (!data?.length || isFetchingMore) {
    return false
  }
  const firstClinicPeriod = Object.values(currentPeriod)[0]
  const firstClinicLastLoadedMonth = Object.values(data[data.length - 1])[0]
  const dataAlreadyFetched = data.some((clinicsAvailabilities) =>
    Object.values(clinicsAvailabilities).some((clinicAvailability) =>
      isAfter(new Date(clinicAvailability.toDate), new Date(periodRangeStart || ''))
    )
  )
  if (!dataAlreadyFetched) {
    return true
  }

  if (!isEmpty(firstClinicPeriod)) {
    return shouldFetchMoreWithCurrentPeriod(firstClinicPeriod, firstClinicLastLoadedMonth)
  }

  return shouldFetchMoreWithoutCurrentPeriod(firstClinicLastLoadedMonth, selectionDate)
}
